import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { APPLICATION_GRID_PREVIEW_LENGTH } from '@absurd-careers/shared';
import type {
  AdminApplicationDetail,
  AdminApplicationGrid,
  AdminApplicationGridRow,
  AdminApplicationListQuery,
  AdminApplicationSummary,
  AdminContactMessageDetail,
  AdminContactMessageListQuery,
  AdminContactMessageSummary,
  AdminDashboardDto,
  AdminJobDetail,
  AdminJobSummary,
  AdminSessionDto,
  ApplicationNoteInput,
  ApplicationStatus,
  ApplicationStatusUpdateInput,
  ContactMessageNoteInput,
  ContactMessageStatusUpdateInput,
  JobCreateInput,
  JobStatus,
  Paginated,
  PublicJobDetail,
  PublicJobSummary,
  QuestionInput,
} from '@absurd-careers/shared';
import { API_BASE_URL, apiRequest, setCsrfToken, toQuery } from './api.js';

export const queryKeys = {
  publicJobs: ['public', 'jobs'] as const,
  publicJob: (slug: string) => ['public', 'job', slug] as const,
  session: ['admin', 'session'] as const,
  dashboard: ['admin', 'dashboard'] as const,
  adminJobs: (filters: Record<string, unknown>) => ['admin', 'jobs', filters] as const,
  adminJob: (id: string) => ['admin', 'job', id] as const,
  adminJobPreview: (id: string) => ['admin', 'job', id, 'preview'] as const,
  applications: (filters: Record<string, unknown>) => ['admin', 'applications', filters] as const,
  applicationGrid: ['admin', 'applications', 'grid'] as const,
  application: (id: string) => ['admin', 'application', id] as const,
  contactMessages: (filters: Record<string, unknown>) => ['admin', 'contact-messages', filters] as const,
  contactMessage: (id: string) => ['admin', 'contact-message', id] as const,
};

// --- Public ----------------------------------------------------------------

export function usePublicJobs() {
  return useQuery({
    queryKey: queryKeys.publicJobs,
    queryFn: () => apiRequest<{ items: PublicJobSummary[] }>('/api/jobs'),
    staleTime: 60_000,
  });
}

export function usePublicJob(slug: string, options?: Partial<UseQueryOptions<PublicJobDetail>>) {
  return useQuery({
    queryKey: queryKeys.publicJob(slug),
    queryFn: () => apiRequest<PublicJobDetail>('/api/jobs/' + encodeURIComponent(slug)),
    retry: false,
    ...options,
  });
}

// --- Admin session ---------------------------------------------------------

export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: async () => {
      const session = await apiRequest<AdminSessionDto>('/api/admin/me');
      setCsrfToken(session.csrfToken);
      return session;
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const session = await apiRequest<AdminSessionDto>('/api/admin/auth/login', {
        method: 'POST',
        body: input,
      });
      setCsrfToken(session.csrfToken);
      return session;
    },
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session, session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<{ ok: boolean }>('/api/admin/auth/logout', { method: 'POST' }),
    onSettled: () => {
      setCsrfToken(null);
      queryClient.clear();
    },
  });
}

// --- Admin data ------------------------------------------------------------

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => apiRequest<AdminDashboardDto>('/api/admin/dashboard'),
  });
}

export function useAdminJobs(filters: { status?: JobStatus; q?: string; page?: number }) {
  return useQuery({
    queryKey: queryKeys.adminJobs(filters),
    queryFn: () =>
      apiRequest<Paginated<AdminJobSummary>>('/api/admin/jobs' + toQuery({ ...filters, pageSize: 25 })),
    placeholderData: (previous) => previous,
  });
}

export function useAdminJob(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminJob(id ?? ''),
    queryFn: () => apiRequest<AdminJobDetail>('/api/admin/jobs/' + id),
    enabled: Boolean(id),
  });
}

export function useAdminJobPreview(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminJobPreview(id ?? ''),
    queryFn: () => apiRequest<PublicJobDetail>('/api/admin/jobs/' + id + '/preview'),
    enabled: Boolean(id) && enabled,
  });
}

/**
 * Every job mutation returns the whole job, so the cache is seeded from the
 * response rather than refetched, and lists are invalidated behind it.
 */
function useJobMutation<TInput>(
  request: (input: TInput) => Promise<AdminJobDetail>,
  options: { invalidateLists?: boolean } = { invalidateLists: true },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (job) => {
      queryClient.setQueryData(queryKeys.adminJob(job.id), job);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminJobPreview(job.id) });
      if (options.invalidateLists !== false) {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
        void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
        void queryClient.invalidateQueries({ queryKey: ['public'] });
      }
    },
  });
}

export function useCreateJob() {
  return useJobMutation<JobCreateInput>((input) =>
    apiRequest<AdminJobDetail>('/api/admin/jobs', { method: 'POST', body: input }),
  );
}

export function useUpdateJob(id: string) {
  return useJobMutation<JobCreateInput>((input) =>
    apiRequest<AdminJobDetail>('/api/admin/jobs/' + id, { method: 'PUT', body: input }),
  );
}

export type JobLifecycleAction = 'publish' | 'close' | 'archive' | 'draft';

export function useJobLifecycle(id: string) {
  return useJobMutation<JobLifecycleAction>((action) =>
    apiRequest<AdminJobDetail>('/api/admin/jobs/' + id + '/' + action, { method: 'POST' }),
  );
}

export function useDuplicateJob(id: string) {
  return useJobMutation<void>(() =>
    apiRequest<AdminJobDetail>('/api/admin/jobs/' + id + '/duplicate', { method: 'POST', body: {} }),
  );
}

export function useDeleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>('/api/admin/jobs/' + id, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useAddQuestion(jobId: string) {
  return useJobMutation<QuestionInput>(
    (input) =>
      apiRequest<AdminJobDetail>('/api/admin/jobs/' + jobId + '/questions', {
        method: 'POST',
        body: input,
      }),
    { invalidateLists: false },
  );
}

export function useUpdateQuestion(jobId: string) {
  return useJobMutation<{ questionId: string; input: QuestionInput }>(
    ({ questionId, input }) =>
      apiRequest<AdminJobDetail>('/api/admin/jobs/' + jobId + '/questions/' + questionId, {
        method: 'PUT',
        body: input,
      }),
    { invalidateLists: false },
  );
}

export function useDeleteQuestion(jobId: string) {
  return useJobMutation<string>(
    (questionId) =>
      apiRequest<AdminJobDetail>('/api/admin/jobs/' + jobId + '/questions/' + questionId, {
        method: 'DELETE',
      }),
    { invalidateLists: false },
  );
}

export function useReorderQuestions(jobId: string) {
  return useJobMutation<string[]>(
    (questionIds) =>
      apiRequest<AdminJobDetail>('/api/admin/jobs/' + jobId + '/questions/reorder', {
        method: 'POST',
        body: { questionIds },
      }),
    { invalidateLists: false },
  );
}

// --- Applications ----------------------------------------------------------

export type ApplicationFilters = Partial<AdminApplicationListQuery>;

export function useApplications(filters: ApplicationFilters) {
  return useQuery({
    queryKey: queryKeys.applications(filters),
    queryFn: () =>
      apiRequest<Paginated<AdminApplicationSummary>>('/api/admin/applications' + toQuery(filters)),
    placeholderData: (previous) => previous,
  });
}

export function useApplication(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.application(id ?? ''),
    queryFn: () => apiRequest<AdminApplicationDetail>('/api/admin/applications/' + id),
    enabled: Boolean(id),
  });
}

function useApplicationMutation<TInput>(
  id: string,
  request: (input: TInput) => Promise<AdminApplicationDetail>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (application) => {
      queryClient.setQueryData(queryKeys.application(id), application);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateApplicationStatus(id: string) {
  return useApplicationMutation<ApplicationStatusUpdateInput>(id, (input) =>
    apiRequest<AdminApplicationDetail>('/api/admin/applications/' + id + '/status', {
      method: 'POST',
      body: input,
    }),
  );
}

export function useAddApplicationNote(id: string) {
  return useApplicationMutation<ApplicationNoteInput>(id, (input) =>
    apiRequest<AdminApplicationDetail>('/api/admin/applications/' + id + '/notes', {
      method: 'POST',
      body: input,
    }),
  );
}

export function useApplicationGrid() {
  return useQuery({
    queryKey: queryKeys.applicationGrid,
    queryFn: () => apiRequest<AdminApplicationGrid>('/api/admin/applications/grid'),
  });
}

export interface GridStatusChange {
  id: string;
  status: ApplicationStatus;
  note?: string;
}

/**
 * Status edits from the spreadsheet show immediately and roll back if the
 * server refuses. The server's reply is then patched into the row, so the whole
 * grid is not refetched for one cell.
 */
export function useGridStatusUpdate() {
  const queryClient = useQueryClient();
  const key = queryKeys.applicationGrid;

  const patchRow = (id: string, patch: Partial<AdminApplicationGridRow>) =>
    queryClient.setQueryData<AdminApplicationGrid>(key, (grid) =>
      grid ? { ...grid, rows: grid.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) } : grid,
    );

  return useMutation({
    mutationFn: ({ id, status, note }: GridStatusChange) =>
      apiRequest<AdminApplicationDetail>('/api/admin/applications/' + id + '/status', {
        method: 'POST',
        body: { status, note: note || undefined },
      }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AdminApplicationGrid>(key);
      patchRow(id, { status, statusChangedAt: new Date().toISOString() });
      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (application) => {
      queryClient.setQueryData(queryKeys.application(application.id), application);
      const lastEvent = application.statusEvents[0];
      const lastNote = application.notes[0];
      patchRow(application.id, {
        status: application.status,
        statusChangedAt: lastEvent?.createdAt ?? application.submittedAt,
        statusChangedBy: lastEvent?.actor?.name ?? null,
        noteCount: application.notes.length,
        latestNote: lastNote
          ? {
              preview: lastNote.body.replace(/\s+/g, ' ').trim().slice(0, APPLICATION_GRID_PREVIEW_LENGTH),
              createdAt: lastNote.createdAt,
              author: lastNote.author?.name ?? null,
            }
          : null,
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'applications'],
        predicate: (query) => query.queryKey[2] !== 'grid',
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

/**
 * Both are followed as ordinary links rather than fetched, so they carry the
 * base URL explicitly for deployments that split the API onto its own origin.
 */
export function applicationsExportUrl(filters: ApplicationFilters): string {
  return API_BASE_URL + '/api/admin/applications/export' + toQuery(filters);
}

export function resumeDownloadUrl(applicationId: string): string {
  return API_BASE_URL + '/api/admin/applications/' + applicationId + '/resume';
}

// --- Contact messages -----------------------------------------------------

export type ContactMessageFilters = Partial<AdminContactMessageListQuery>;

export function useContactMessages(filters: ContactMessageFilters) {
  return useQuery({
    queryKey: queryKeys.contactMessages(filters),
    queryFn: () =>
      apiRequest<Paginated<AdminContactMessageSummary>>('/api/admin/contact-messages' + toQuery(filters)),
    placeholderData: (previous) => previous,
  });
}

export function useContactMessage(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.contactMessage(id ?? ''),
    queryFn: () => apiRequest<AdminContactMessageDetail>('/api/admin/contact-messages/' + id),
    enabled: Boolean(id),
  });
}

function useContactMessageMutation<TInput>(
  id: string,
  request: (input: TInput) => Promise<AdminContactMessageDetail>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (message) => {
      queryClient.setQueryData(queryKeys.contactMessage(id), message);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'contact-messages'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateContactMessageStatus(id: string) {
  return useContactMessageMutation<ContactMessageStatusUpdateInput>(id, (input) =>
    apiRequest<AdminContactMessageDetail>('/api/admin/contact-messages/' + id + '/status', {
      method: 'POST',
      body: input,
    }),
  );
}

export function useAddContactMessageNote(id: string) {
  return useContactMessageMutation<ContactMessageNoteInput>(id, (input) =>
    apiRequest<AdminContactMessageDetail>('/api/admin/contact-messages/' + id + '/notes', {
      method: 'POST',
      body: input,
    }),
  );
}
